/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { IAttachment, ICoding, IReference } from '@ahryman40k/ts-fhir-types/lib/R4';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import clsx from 'clsx';

import { useAppSelector } from '../../../app/hooks';
import { FhirPredefinedWidget, fhirTypesMap, HtmlFhirInstanceGrouped } from '../algorithm/types';
import { WideTooltip } from '../chat/components/WideTooltip';
import { formatDate, makeGrayDummyTextBlocks } from '../utils';
import {
  IDiagnosticReportSimplified,
  IObservationSimplified,
  IObservationWithPearlCode,
  ObservationValueInterpretationEnum,
} from './fhir_types';
import {
  DiagnosticReportAndObservations,
  getCpt4Code,
  getInterpretationFromCode,
  getLoincCode,
  getOurObservationsIndexMap,
  isObservationFetched,
  isObservationsWithInDateRange,
  noResultFoundMsg,
  getFhirRuleConfigurationError,
  sortFilterDiagnosticReportObservationsGrouped,
} from './fhir_utils';
import { getCodeableConceptText } from './codeableConcept';
import { FhirHighlight } from './FhirHighlight';
import {
  selectDateRange,
  selectLoadingPredefinedWidgets,
  selectFhirHighlightForId,
  selectGroupedObservationsMapById,
  selectFhirPatientServer,
} from './fhirSlice';
import styles from './FhirWidget.module.scss';
import { FhirWidgetDateSelector } from './FhirWidgetDateSelector';
import { FhirWidgetPanel } from './FhirWidgetPanel';
import { FhirWidgetRangeTooltip } from './FhirWidgetRangeTooltip';
import { HighInterpretationIcon } from './HighInterpretationIcon';
import LowInterpretationIcon from './LowInterpretationIcon';
import { useFhirPatientDataAndAutoloadObservations, useShouldHighlightObservationFunc } from './useFhirHooks';
import { selectPearlUserInfo } from '../Login/userSlice';
import { analyticsTrack } from '../analytics_utils';
import { getObservationValue } from './FhirWidgetObservationValue';
import PresentedFormViewTooltip from './PresentedFormViewTooltip';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { selectCreatorMode } from '../chat/chatSlice';

export type FhirWidgetProps = {
  fhir: HtmlFhirInstanceGrouped;
  readOnly?: boolean;
  onUpdate: () => void;
  noPatient?: boolean /* If we are viewing this when editing a guide with ckeditor */;
  children?: React.ReactNode;
  pageId?: string;
  documentVersionId?: string;
  inEditorMode?: boolean;
};

/** One of the panels in the contextualization sidebar (e.g. the "CMP" panel).
 *  Multiple <fhir> tags could be pointing to a single FhirWidget if they have the same predefinedWidget
 *  And a single <fhir> tag could point to multiple FhirWidget if it has multiple fhirWidgetIds
 */
export function FhirWidget({
  fhir,
  readOnly,
  onUpdate,
  noPatient,
  pageId,
  documentVersionId,
  children,
  inEditorMode,
}: FhirWidgetProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [historicalIndex, setHistoricalIndex] = React.useState(0);

  const { loadingPanel, loadingObservations, panelWithObservations } = useFhirPatientDataAndAutoloadObservations(
    fhir.predefinedWidget,
    onUpdate,
    historicalIndex
  );
  const loadingPredefinedWidgets = useAppSelector(selectLoadingPredefinedWidgets);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const loading = loadingPanel || loadingObservations || loadingPredefinedWidgets;
  const groupedObservations = useAppSelector(selectGroupedObservationsMapById);

  const dateRange = useAppSelector(selectDateRange);
  const hasData = panelWithObservations !== undefined;
  const fhirConfig = fhir.predefinedWidget;
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    //setAnchorEl(event.currentTarget);
  };
  const { blacklist, whitelist, rules } = fhirConfig || {};
  const whitelistIndexMap = useMemo(() => getOurObservationsIndexMap(whitelist), [whitelist]);
  const blacklistIndexMap = useMemo(() => getOurObservationsIndexMap(blacklist), [blacklist]);
  let afterFilter = panelWithObservations;
  let result: any = [];
  if (dateRange) {
    // dateFilter only for consumer
    panelWithObservations?.map((o) => {
      const dateTime = isObservationsWithInDateRange(
        o.diagnosticReport.effectiveDateTime ||
          (o.diagnosticReport.result?.[0] as IObservationSimplified | undefined)?.effectiveDateTime,
        dateRange
      );
      result?.push(dateTime ? o : null);
      afterFilter = result?.filter((list: IObservationSimplified) => list != null);
      return afterFilter;
    });
  }
  const panelObservationsSortedAndFiltered = useMemo(
    () =>
      sortFilterDiagnosticReportObservationsGrouped(
        afterFilter,
        whitelistIndexMap,
        whitelist,
        blacklistIndexMap,
        blacklist,
        true,
        true
      ),
    [afterFilter, whitelistIndexMap, whitelist, blacklistIndexMap, blacklist]
  );
  const hasVisiblePanels = panelObservationsSortedAndFiltered?.some((panels) =>
    panels.some((panel) => !panel.hidePanelFromDateSelector)
  );

  const noResultMsg = useMemo(
    () =>
      noPatient ? '' : hasData ? noResultFoundMsg(fhirConfig, !!fhir?.predefinedWidgetId) : loading ? '' : 'No patient',
    [noPatient, hasData, fhirConfig, fhir, loading]
  );
  const fhirTypeInfo = fhirTypesMap.get(fhirConfig ? fhirConfig?.type || 'Labs' : 'Not Set');

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'simple-popover' : undefined;
  const title = fhirConfig?.title || fhirConfig?.name;
  const predefinedWidgetId = fhir.predefinedWidgetId;
  const predefinedWidgetIds = useMemo(() => (predefinedWidgetId ? [predefinedWidgetId] : []), [predefinedWidgetId]);
  const configurationError = getFhirRuleConfigurationError(rules);

  if (!inEditorMode && !fhir.predefinedWidgetId) {
    return null; // Don't show anything in consumer mode if not configured
  }

  if (loadingPredefinedWidgets) {
    return null; // Don't show anything until we've loaded the widgets to know what to show
  }

  if (configurationError) {
    return (
      <FhirHighlight
        fhirWidgetIds={fhir.fhirWidgetIds}
        fhirPredefinedWidgetIds={predefinedWidgetIds}
        color={fhirTypeInfo?.darkenedBorderColor}
        documentVersionId={documentVersionId}
        pageId={pageId}
        inEditorMode={inEditorMode}
      >
        <FhirWidgetPanel
          color={fhirTypeInfo?.legendColor}
          overrideTitle={title}
          isWip={fhirConfig?.wip}
          isDeleted={fhirConfig?.deleted}
          onUpdate={onUpdate}
          hideAccordionActions
        >
          <div className={styles.configurationError} role="alert">
            <strong>Widget unavailable due to a configuration error.</strong>
            {isCreatorMode && <div>{configurationError}</div>}
          </div>
        </FhirWidgetPanel>
        {children}
      </FhirHighlight>
    );
  }

  if (loading && !noPatient && !hasVisiblePanels) {
    // Still loading, and have no results yet, so show nothing but a linear progress indicator
    return (
      <FhirHighlight
        fhirWidgetIds={fhir.fhirWidgetIds}
        fhirPredefinedWidgetIds={predefinedWidgetIds}
        className={styles.progressDiv}
        color={fhirTypeInfo?.darkenedBorderColor}
        documentVersionId={documentVersionId}
        pageId={pageId}
        inEditorMode={inEditorMode}
      >
        <FhirWidgetPanel
          color={fhirTypeInfo?.legendColor}
          overrideTitle={title}
          isWip={fhirConfig?.wip}
          isDeleted={fhirConfig?.deleted}
          showGFRDisclaimer={fhirConfig?.showGFRDisclaimer}
          onUpdate={onUpdate}
        >
          <div className={styles.entry}>
            {/* Show loader here */}
            <CircularProgress
              style={{ color: fhirTypeInfo?.legendColor || '#3d3a62', width: 37, height: 37, margin: 'auto' }}
            />
          </div>
        </FhirWidgetPanel>
      </FhirHighlight>
    );
  }

  // No observations match (and we've finished loading or there are no rules)
  if (noPatient || (!loading && !hasVisiblePanels)) {
    return (
      <FhirHighlight
        fhirWidgetIds={fhir.fhirWidgetIds}
        color={fhirTypeInfo?.darkenedBorderColor}
        fhirPredefinedWidgetIds={predefinedWidgetIds}
        documentVersionId={documentVersionId}
        pageId={pageId}
        inEditorMode={inEditorMode}
      >
        <FhirWidgetPanel
          color={fhirTypeInfo?.legendColor}
          overrideTitle={title}
          isWip={fhirConfig?.wip}
          isDeleted={fhirConfig?.deleted}
          showGFRDisclaimer={fhirConfig?.showGFRDisclaimer}
          onUpdate={onUpdate}
          compact={noPatient}
          hideAccordionActions
        >
          <div className={styles.entry}>
            <table className={styles.table}>
              <tbody>
                {fhirConfig?.msgDescription && (
                  <tr>
                    <td className={fhirConfig?.msgDescription ? styles.msgDescription : ''}>
                      {fhirConfig?.msgDescription}
                    </td>
                  </tr>
                )}
                {rules ? (
                  <>
                    <tr>
                      <td className={styles.noResultMsg}>
                        {noResultMsg}
                        {inEditorMode && !fhir.predefinedWidgetId && `${noResultMsg ? ', ' : ''}No widget configured`}
                      </td>
                    </tr>
                    {inEditorMode &&
                      fhir?.observationGroupIds?.length > 0 &&
                      fhir.observationGroupIds.map((observationGroupId, index) => (
                        <tr
                          key={index}
                          data-observationgroupid={observationGroupId}
                          className={clsx(styles.border, styles.observationHighlight)}
                        >
                          <td>{groupedObservations?.[observationGroupId]?.name}</td>
                        </tr>
                      ))}
                  </>
                ) : fhir.predefinedWidgetId ? (
                  <tr>
                    <td className={styles.msgDescription}>
                      {!inEditorMode ? (
                        'Missing configuration' // Change verbiage for consumer mode to be short and simple
                      ) : (
                        <>
                          Could not find widget (ID: {fhir.predefinedWidgetId}) - Maybe this environment has not been
                          updated with this widget?
                        </>
                      )}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td className={styles.msgDescription}>Not yet configured</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </FhirWidgetPanel>
        {children}
      </FhirHighlight>
    );
  }

  return (
    <>
      {panelObservationsSortedAndFiltered?.map((panelWithObservations, index) => (
        <FhirWidgetPanelWithObservations
          key={index}
          panelWithObservations={panelWithObservations}
          legendColor={fhirTypeInfo?.legendColor}
          darkenedBorderColor={fhirTypeInfo?.darkenedBorderColor}
          handleClick={handleClick}
          fhirWidgetIds={fhir.fhirWidgetIds}
          fhirObservationGroupIds={fhir.observationGroupIds}
          predefinedWidgetId={predefinedWidgetId}
          onUpdate={onUpdate}
          noResultMsg={noResultMsg}
          fhirConfig={fhirConfig}
          loadingPanel={loadingPanel}
          historicalIndex={historicalIndex}
          setHistoricalIndex={setHistoricalIndex}
          children={children}
          pageId={pageId}
          documentVersionId={documentVersionId}
          inEditorMode={inEditorMode}
        />
      ))}
      <Popover
        open={open}
        onClose={handleClose}
        id={popoverId}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className={styles.popover}>
          {/*data.entry.map((entry, index) => (
            <div key={index}>
              <div>
                <b>{entry.resource.subject.display}</b>
              </div>
              <div>{formatDateTime(entry.resource.effectiveDateTime)}</div>
              <div>{entry.resource.encounter.display}</div>
              <br />
              <span className={styles.firstPanel}>
                <b>{entry.resource.code.text}</b>
                <br />
                <span className={styles.key}>Value:</span> {entry.resource.valueQuantity.comparator}
                {entry.resource.valueQuantity.value}
                {entry.resource.valueQuantity.unit}
                <br />
                <span className={styles.key}>Reference:</span>{' '}
                {entry.resource.referenceRange.map((ref) => ref.text).join(', ')}
                <br />
                <span className={styles.key}>Notes:</span> {entry.resource.note.map((note) => note.text).join(', ')}
              </span>
            </div>
          ))*/}
        </div>
      </Popover>
    </>
  );
}

interface FhirWidgetPanelWithObservationsProps {
  panelWithObservations: DiagnosticReportAndObservations[];
  legendColor?: string;
  darkenedBorderColor?: string;
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  fhirWidgetIds?: string[];
  predefinedWidgetId?: string;
  noResultMsg?: string;
  onUpdate?: () => void;
  fhirConfig?: FhirPredefinedWidget;
  loadingPanel?: boolean;
  loadingProgress?: JSX.Element | null;
  historicalIndex: number;
  setHistoricalIndex: (index: number) => void;
  children?: React.ReactNode;
  pageId?: string;
  documentVersionId?: string;
  fhirObservationGroupIds?: string[];
  inEditorMode?: boolean;
}

/** How many pulsing gray boxes to show, maximum, when
 *  loading observations for a panel */
const NUM_DUMMY_OBSERVATIONS_WHEN_LOADING = 3;

/** Show a panel.  We could have multiple fhirWidgetId's pointing to the same FhirWidgetPanelWithObservations.
 */
function FhirWidgetPanelWithObservations({
  legendColor,
  darkenedBorderColor,
  panelWithObservations,
  handleClick,
  fhirWidgetIds,
  fhirObservationGroupIds,
  predefinedWidgetId,
  noResultMsg,
  fhirConfig,
  onUpdate,
  loadingPanel,
  loadingProgress,
  historicalIndex,
  setHistoricalIndex,
  children,
  pageId,
  documentVersionId,
  inEditorMode,
}: FhirWidgetPanelWithObservationsProps) {
  const msgDescription = fhirConfig?.msgDescription;
  const [expanded, setExpanded] = React.useState(false);
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);

  const [seeHiddenObervations, setSeeHiddenObservations] = React.useState(false);
  const panelObs = panelWithObservations[historicalIndex];
  const lookbackTimeMonths = fhirConfig?.lookbackTimeMonths;
  const ageMonths = panelObs?.ageMonths;
  const fhirWidgetId = fhirWidgetIds?.[0];
  const highlight = useAppSelector((state) =>
    selectFhirHighlightForId(state, fhirWidgetIds!, predefinedWidgetId ? [predefinedWidgetId] : [])
  );
  const observationGroupIds = highlight?.predefinedWidgetsHighlighted
    ?.flatMap((pw) => pw?.predefinedWidgets?.map((o) => o?.observationGroup?.id))
    .filter((id): id is string => !!id);
  const { shouldHighlightObservationFunc } = useShouldHighlightObservationFunc(
    highlight ? observationGroupIds : fhirObservationGroupIds
  );

  useLayoutEffect(() => {
    onUpdate?.(); // Relayout when the height of the panel could have changed
  }, [historicalIndex, onUpdate, seeHiddenObervations, panelWithObservations.length]);

  useLayoutEffect(() => {
    let newHistoricalIndex = historicalIndex;
    const max = expanded ? panelWithObservations.length - 1 : 3;
    newHistoricalIndex = Math.max(0, Math.min(newHistoricalIndex, max));
    while (
      panelWithObservations[newHistoricalIndex]?.isLoaded &&
      panelWithObservations[newHistoricalIndex]?.hidePanelFromDateSelector
    ) {
      newHistoricalIndex += 1;
    }
    newHistoricalIndex = Math.max(0, Math.min(newHistoricalIndex, max));
    if (newHistoricalIndex !== historicalIndex) {
      setHistoricalIndex(newHistoricalIndex);
    }
  }, [
    historicalIndex,
    setHistoricalIndex,
    panelWithObservations,
    panelWithObservations.length,
    expanded,
    fhirConfig?.lookbackTimeMonths,
  ]);

  let hiddenObservationList = panelObs?.hiddenObservationList;
  let observationList = panelObs?.observationList;
  if (observationList && observationList.length === 0 && !panelObs?.hideAllIfNoWhitelistMatches) {
    // If observationList is empty, then use hiddenObservationList and so just show everything
    observationList = hiddenObservationList;
    hiddenObservationList = [];
  }
  const [notRelevantClicked, setNotRelevantClicked] = useState(false);
  const handleNotRelevantClicked = useCallback(() => {
    setNotRelevantClicked(true);
    analyticsTrack(
      'widget_not_relevant_click',
      {
        diagnosticReportText: panelObs?.diagnosticReport?.code?.text,
        loinc: getLoincCode(panelObs?.diagnosticReport?.code?.coding),
        cpt: getCpt4Code(panelObs?.diagnosticReport?.code?.coding),
        isCareEverywhere: panelObs?.diagnosticReport?.isCareEverywhere,
        fhirWidgetId: fhirWidgetId,
        fhirWidgetName: fhirConfig?.name,
      },
      pearlUserInfo
    );
  }, [
    fhirConfig?.name,
    fhirWidgetId,
    panelObs?.diagnosticReport?.code?.coding,
    panelObs?.diagnosticReport?.code?.text,
    panelObs?.diagnosticReport?.isCareEverywhere,
    pearlUserInfo,
  ]);
  const enableNotRelevantButton = false;
  const notRelevantButton = enableNotRelevantButton ? (
    <Button
      variant="styledToggle"
      size="small"
      title={
        !notRelevantClicked
          ? 'Click to send feedback that this information is not relevant'
          : 'Feedback sent, thank you!'
      }
      className={!notRelevantClicked ? styles.notRelevantButton : styles.notRelevantButtonClicked}
      onClick={handleNotRelevantClicked}
    >
      {!notRelevantClicked ? <>Click here if this test is not relevant</> : <>Feedback&nbsp;sent, thank you!</>}
    </Button>
  ) : null;

  if (panelObs?.diagnosticReport?.presentedForm?.length) {
    console.log('presentedForm found', panelObs?.diagnosticReport?.presentedForm);
  }

  return (
    <FhirHighlight
      fhirWidgetIds={fhirWidgetIds}
      color={darkenedBorderColor}
      fhirPredefinedWidgetIds={predefinedWidgetId ? [predefinedWidgetId] : []}
      documentVersionId={documentVersionId}
      pageId={pageId}
      inEditorMode={inEditorMode}
    >
      <FhirWidgetPanel
        diagnosticReport={panelObs?.diagnosticReport}
        overrideTitle={fhirConfig?.title}
        isWip={fhirConfig?.wip}
        isDeleted={fhirConfig?.deleted}
        titleNote={fhirConfig?.titleNote}
        showGFRDisclaimer={fhirConfig?.showGFRDisclaimer}
        color={legendColor}
        onUpdate={onUpdate}
      >
        <div className={styles.entry} onClick={handleClick}>
          <div className={msgDescription ? styles.msgDescription2 : ''}>{msgDescription}</div>
          <FhirWidgetDateSelector
            panelWithObservations={panelWithObservations}
            historicalIndex={historicalIndex}
            setHistoricalIndex={setHistoricalIndex}
            showLoadingIndicator={loadingPanel}
            onUpdate={onUpdate}
            lookbackTimeMonths={fhirConfig?.lookbackTimeMonths}
            setExpanded={setExpanded}
            expanded={expanded}
            hasFullYear
          />
          {lookbackTimeMonths && lookbackTimeMonths > 0 && ageMonths && ageMonths > lookbackTimeMonths ? (
            <div className={styles.oldWarning}>
              Older than {lookbackTimeMonths} month{lookbackTimeMonths > 1 && 's'}
            </div>
          ) : null}
          {fhirConfig?.title && panelObs?.diagnosticReport?.code?.text ? (
            /* If we have an override title, then we won't be showing the diagnosticReport?.code?.text in the
               panel title, so show it here instead */
            <div className={styles.title}>{panelObs?.diagnosticReport?.code?.text}</div>
          ) : null}
          <table className={styles.table} data-index={historicalIndex}>
            <tbody>
              {observationList?.map((o, index) =>
                index < NUM_DUMMY_OBSERVATIONS_WHEN_LOADING || isObservationFetched(o) ? (
                  <tr
                    key={index}
                    data-observationgroupid={o.observationGroupId}
                    className={clsx(
                      `${observationList && observationList?.length > 1 && styles.border}`,
                      shouldHighlightObservationFunc(o) ? styles.observationHighlight : undefined
                    )}
                  >
                    <FhirWidgetObservationTd
                      diagnosticReport={panelObs?.diagnosticReport}
                      observation={o}
                      key={index}
                      willLoadMissingObservations
                    />
                  </tr>
                ) : null
              )}
              {observationList && observationList?.length === 0 && (
                <tr>
                  <td className={styles.noResultMsg}>{noResultMsg}</td>
                </tr>
              )}
              {hiddenObservationList && hiddenObservationList?.length > 0 ? (
                seeHiddenObervations ? (
                  <>
                    <tr>
                      <td colSpan={2} style={{ padding: 0 }} className={styles.buttonRow}>
                        <Button variant="styledToggle" size="small" onClick={() => setSeeHiddenObservations(false)}>
                          Collapse view
                        </Button>
                      </td>
                    </tr>
                    {hiddenObservationList.map((o, index) => (
                      <tr
                        key={index}
                        className={clsx(
                          styles.wasHiddenObservation,
                          shouldHighlightObservationFunc(o) ? styles.observationHighlight : undefined
                        )}
                      >
                        <FhirWidgetObservationTd
                          diagnosticReport={panelObs?.diagnosticReport}
                          observation={o}
                          key={index}
                          willLoadMissingObservations
                        />
                      </tr>
                    ))}
                    {/* If we have more than 10 hidden observations, add a collapse button at the end of the list */}
                    {hiddenObservationList.length > 10 && (
                      <tr>
                        <td colSpan={2} style={{ padding: 0 }} className={styles.buttonRow}>
                          <Button variant="styledToggle" size="small" onClick={() => setSeeHiddenObservations(false)}>
                            Collapse view
                          </Button>
                        </td>
                      </tr>
                    )}
                  </>
                ) : notRelevantButton ? (
                  <tr>
                    <td colSpan={2} style={{ padding: 0 }} className={styles.buttonRow}>
                      <Button variant="styledToggle" size="small" onClick={() => setSeeHiddenObservations(true)}>
                        Show all
                      </Button>
                    </td>
                    <td>{notRelevantButton}</td>
                  </tr>
                ) : null
              ) : notRelevantButton ? (
                <tr>
                  <td colSpan={2} style={{ padding: 0 }} className={styles.buttonRow}>
                    {notRelevantButton}
                  </td>
                </tr>
              ) : null}
              {panelObs?.diagnosticReport?.presentedForm?.map((form, i) => (
                <tr key={i}>
                  <FhirWidgetPresentedFormTd form={form} />
                </tr>
              ))}
            </tbody>
          </table>
          {!panelWithObservations?.length && loadingProgress}
        </div>
      </FhirWidgetPanel>
      {children}
    </FhirHighlight>
  );
}

export function FhirWidgetPresentedFormTd({ form }: { form: IAttachment }) {
  const server = useAppSelector(selectFhirPatientServer);
  return (
    <td colSpan={2} style={{ paddingTop: '10px' }}>
      {server ? (
        <PresentedFormViewTooltip presentedForm={form} mrn={'testing_fake_mrn'} server={server}>
          <span>
            <DescriptionOutlinedIcon />
            <span style={{ marginLeft: '4px', textDecoration: 'underline dashed' }}>View Attachment</span>
          </span>
        </PresentedFormViewTooltip>
      ) : (
        /* should be unreachable */
        <span>Attachment available (Can't view)</span>
      )}
    </td>
  );
}
/**
 * Note this is used from FhirWidgetPreview and FhirWidgetFlipped
 */
export function FhirWidgetObservationTd({
  diagnosticReport,
  observation,
  header,
  willLoadMissingObservations,
  showLoincCodeTooltip,
}: {
  diagnosticReport?: IDiagnosticReportSimplified;
  observation?: IObservationWithPearlCode;
  header?: boolean;
  willLoadMissingObservations?: boolean; // Whether to show a loading indicator if we haven't loaded the observation yet
  showLoincCodeTooltip?: boolean;
}) {
  function RenderPdf({ observation }: { observation: any }) {
    return null;
    /*
    if (observation.presentedForm?.[0]?.contentType !== 'application/pdf') return null;
    const pdfData = observation.presentedForm?.[0]?.data;
    return (
      <object data={`data:application/pdf;base64,${pdfData}`} type="application/pdf">
        Embedded pdf
      </object>
    );
    */
  }

  /** See https://terminology.hl7.org/5.1.0/CodeSystem-v3-ObservationInterpretation.html */
  function getInterpretation(coding: ICoding | undefined) {
    const interpretation = getInterpretationFromCode(coding?.code);
    if (!interpretation || interpretation === ObservationValueInterpretationEnum.NORMAL) return null;
    if (interpretation === ObservationValueInterpretationEnum.HIGH)
      return <HighInterpretationIcon className={styles.interpretationIcon} />;
    if (interpretation === ObservationValueInterpretationEnum.LOW)
      return <LowInterpretationIcon className={styles.interpretationIcon} />;

    return <PriorityHighIcon fontSize="small" style={{ marginBottom: -4, marginTop: -4 }} />;
  }

  function toSubscript(d: string) {
    if (d === '2') return '²';
    if (d === '3') return '³';
    if (d === '4') return '⁴';
    if (d === '5') return '⁵';
    if (d === '6') return '⁶';
    if (d === '7') return '⁷';
    if (d === '8') return '⁸';
    if (d === '9') return '⁹';
    if (d === '10') return '¹⁰';
    if (d === '11') return '¹¹';
    if (d === '12') return '¹²';
    return `^${d}`;
  }

  const [valueRendered] = getObservationValue({ observation });
  const interpretationCoding = observation?.interpretation?.[0]?.coding?.[0];
  const interpretation = getInterpretation(interpretationCoding);
  const notes = observation?.note?.map((note, i) => (
    <div key={i}>
      <b>Note:</b>
      <pre>{note.text}</pre>
    </div>
  ));
  const notesRendered =
    notes && notes.length > 0 ? (
      <WideTooltip arrow title={<div className={styles.noteContent}>{notes}</div>}>
        <span className={styles.noteHoverable}>Note</span>
      </WideTooltip>
    ) : null;
  const fetched = isObservationFetched(observation);
  const reference = (observation as IReference)?.reference;
  const willLoadReference = willLoadMissingObservations && !fetched && !!reference;

  function handleFirstPanelClick(event: React.MouseEvent) {
    // Only if holding shift key
    if (!event.shiftKey) return;
    console.log(observation);
  }

  const dummyText = useMemo(() => makeGrayDummyTextBlocks(), []);
  const dummyText2 = useMemo(() => makeGrayDummyTextBlocks(1, 5), []);
  const observationLabel =
    getCodeableConceptText(observation?.code) || (fetched ? (observation as any).display : dummyText);

  const tooltip = (
    <FhirWidgetRangeTooltip
      codeText={diagnosticReport?.code.text}
      observation={observation}
      observationLabel={observationLabel}
    />
  );

  return (
    /*<Tooltip title={<div dangerouslySetInnerHTML={{ __html: entry.resource?.text?.div }} />} enterDelay={1000}> */
    <>
      <td className={styles.firstPanel} onClick={handleFirstPanelClick}>
        <span
          title={
            !fetched
              ? undefined
              : showLoincCodeTooltip
              ? `Loinc code: ${getLoincCode(observation?.code?.coding) || '(missing)'}`
              : undefined
          }
          className={clsx(
            fetched
              ? undefined
              : willLoadReference
              ? styles.notFetchedLoading
              : styles.notFetched /* we haven't resolved the reference - we have no actual data */,
            styles.observationLabelContainer
          )}
        >
          <b className={styles.observationLabel}>{observationLabel}</b>
          {fetched ? undefined : dummyText2}
          &nbsp;&nbsp;
        </span>
        <span className={clsx(styles.interpretation, styles[`interpretation-${interpretationCoding?.code?.[0]}`])}>
          {header && formatDate(observation?.issued!)}
          {/*observation?.valueQuantity?.comparator*/}
          <RenderPdf observation={observation} />
          <Tooltip title={tooltip} enterDelay={0} arrow disableInteractive>
            <span className={tooltip && styles.hasTooltip}>
              <b>{valueRendered}</b>
              <span className={observation?.valueQuantity?.unit === '%' ? styles.unitPercent : styles.unit}>
                {observation?.valueQuantity?.unit
                  ?.replaceAll('m2', 'm²')
                  ?.replaceAll(/\bx?10[*^E]([0-9][0-9]*)/g, (_: any, d: string) => `x10${toSubscript(d)}`)}
              </span>
              {interpretation}
            </span>
          </Tooltip>
          {notesRendered}
        </span>
      </td>
    </>
  );
}
